import { Dispatch, SetStateAction, useEffect, useMemo, useRef, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import { SettingsForm } from './SettingsModalForm';
import { SettingsModalMain } from './SettingsModalMain';
import { UserPreferences, columnsDefault, widgetsDefault } from '../../../store/User';
import { getFormById, updateForm } from '../../../store/BulkForm';
import { Account } from '../../../types';
import { SettingsModalSubAccountsForm } from './SettingsModalSubAccountsForm';
import { useSnackbar } from '../../../Context/SnackbarContext';

type ModalView = 'main' | 'column' | 'widget' | 'subAccounts';
export interface SettingsModalProps {
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  userPreferences: UserPreferences;
  userName: string | undefined;
  id: string;
  secondaryAccounts?: Account[];
}

export const SettingsModal = ({
  open,
  setOpen,
  userPreferences,
  userName,
  id,
  secondaryAccounts
}: SettingsModalProps) => {
  const [modalViewState, setModalViewState] = useState<Record<ModalView, boolean>>({
    main: true,
    column: false,
    widget: false,
    subAccounts: false
  });
  const [newUserPreferences, setNewUserPreferences] = useState<UserPreferences>(userPreferences);
  const [selectedSecondaryAccounts, setSelectedSecondaryAccounts] = useState<Account[]>([]);
  const [otpRequired, setOtpRequired] = useState<boolean>(true);
  const { setSnack } = useSnackbar();
  const snapshot = useRef<{ preferences: UserPreferences; accounts: Account[]; otpRequired: boolean } | null>(null);
  const subModalPreferencesSnapshot = useRef<UserPreferences | null>(null);
  const subModalAccountsSnapshot = useRef<Account[]>([]);
  const snapshotPrefJson = useRef<string | null>(null);
  const snapshotAccountsJson = useRef<string | null>(null);
  const subModalPrefJson = useRef<string | null>(null);
  const subModalAccountsJson = useRef<string | null>(null);

  const prefJson = useMemo(() => JSON.stringify(newUserPreferences), [newUserPreferences]);
  const accountsJson = useMemo(() => JSON.stringify(selectedSecondaryAccounts), [selectedSecondaryAccounts]);

  const handleSave = () => {
    updateForm(
      id,
      {
        userCustomizations: newUserPreferences,
        accounts: selectedSecondaryAccounts,
        otp_required_for_login: otpRequired
      },
      'user'
    );
    setSnack({ message: 'Settings saved successfully.', type: 'success', open: true });
    navigateModal('main');
    setOpen(false);
  };

  const handleSubModalSave = (label: string) => {
    setSnack({ message: `${label} changes applied.`, type: 'success', open: true });
    navigateModal('main');
  };

  const handleSubModalPreferencesCancel = (label: string) => {
    if (subModalPreferencesSnapshot.current) setNewUserPreferences(subModalPreferencesSnapshot.current);
    setSnack({ message: `${label} changes discarded.`, type: 'info', open: true });
    navigateModal('main');
  };

  const handleSubAccountsCancel = () => {
    setSelectedSecondaryAccounts(subModalAccountsSnapshot.current);
    setSnack({ message: 'Sub-account changes discarded.', type: 'info', open: true });
    navigateModal('main');
  };

  const handleCancel = () => {
    if (snapshot.current) {
      setNewUserPreferences(snapshot.current.preferences);
      setSelectedSecondaryAccounts(snapshot.current.accounts);
      setOtpRequired(snapshot.current.otpRequired);
    }
    setSnack({ message: 'Changes discarded.', type: 'info', open: true });
    navigateModal('main');
    setOpen(false);
  };

  const handleTwoFactorAuthChange = () => {
    setOtpRequired((prev) => !prev);
  };

  const handleSecondaryAccountChange = (accts: Account[] | null) => {
    setSelectedSecondaryAccounts(accts || []);
  };

  const navigateModal = (view: ModalView) => {
    switch (view) {
      case 'column':
        return setModalViewState({ main: false, widget: false, column: true, subAccounts: false });
      case 'widget':
        return setModalViewState({ main: false, widget: true, column: false, subAccounts: false });
      case 'subAccounts':
        return setModalViewState({ main: false, widget: false, column: false, subAccounts: true });
      case 'main':
      default:
        return setModalViewState({ main: true, widget: false, column: false, subAccounts: false });
    }
  };

  useEffect(() => {
    if (!open) return;
    const existingForm = getFormById(id);
    const initialPreferences = structuredClone(userPreferences);
    const initialAccounts = existingForm?.accounts || [];
    const initialOtp = existingForm?.otp_required_for_login ?? true;
    snapshot.current = {
      preferences: structuredClone(initialPreferences),
      accounts: [...initialAccounts],
      otpRequired: initialOtp
    };
    snapshotPrefJson.current = JSON.stringify(initialPreferences);
    snapshotAccountsJson.current = JSON.stringify(initialAccounts);
    setNewUserPreferences(initialPreferences);
    setSelectedSecondaryAccounts(initialAccounts);
    setOtpRequired(initialOtp);
    navigateModal('main');
  }, [open, id]);

  return (
    <Dialog
      open={open}
      disableEscapeKeyDown
      PaperProps={{ sx: { overflowX: 'hidden' } }}
      onClose={(_event, reason) => {
        if (reason === 'backdropClick') return;
        handleCancel();
      }}>
      {modalViewState.column && (
        <SettingsForm
          setNewUserPreferences={setNewUserPreferences}
          customizations={newUserPreferences}
          title="Inventory"
          saveDisabled={prefJson === subModalPrefJson.current}
          onCancel={() => handleSubModalPreferencesCancel('Column')}
          onSave={() => handleSubModalSave('Column')}
        />
      )}
      {modalViewState.widget && (
        <SettingsForm
          customizations={newUserPreferences}
          setNewUserPreferences={setNewUserPreferences}
          title="Widgets"
          saveDisabled={prefJson === subModalPrefJson.current}
          onCancel={() => handleSubModalPreferencesCancel('Widget')}
          onSave={() => handleSubModalSave('Widget')}
        />
      )}
      {modalViewState.subAccounts && secondaryAccounts !== undefined && (
        <SettingsModalSubAccountsForm
          accounts={secondaryAccounts}
          selectedAccounts={selectedSecondaryAccounts}
          handleAccountSelect={handleSecondaryAccountChange}
          saveDisabled={accountsJson === subModalAccountsJson.current}
          onCancel={handleSubAccountsCancel}
          onSave={() => handleSubModalSave('Sub-account')}
        />
      )}
      {modalViewState.main && (
        <SettingsModalMain
          username={userName || ''}
          columnsCustom={!columnsDefault(newUserPreferences.content.inventory_grid_All.muiConfig)}
          widgetsCustom={!widgetsDefault(newUserPreferences.content.widgets)}
          subAccountsCustom={!!selectedSecondaryAccounts.length}
          twoFactorAuth={otpRequired}
          subAccounts={secondaryAccounts}
          saveDisabled={
            snapshot.current === null ||
            (prefJson === snapshotPrefJson.current &&
              accountsJson === snapshotAccountsJson.current &&
              otpRequired === snapshot.current.otpRequired)
          }
          onEditColumns={() => {
            subModalPreferencesSnapshot.current = structuredClone(newUserPreferences);
            subModalPrefJson.current = prefJson;
            navigateModal('column');
          }}
          onEditWidgets={() => {
            subModalPreferencesSnapshot.current = structuredClone(newUserPreferences);
            subModalPrefJson.current = prefJson;
            navigateModal('widget');
          }}
          onEditSubAccounts={() => {
            subModalAccountsSnapshot.current = [...selectedSecondaryAccounts];
            subModalAccountsJson.current = accountsJson;
            navigateModal('subAccounts');
          }}
          onTwoFactorAuthChange={handleTwoFactorAuthChange}
          onCancel={handleCancel}
          onSave={handleSave}
        />
      )}
    </Dialog>
  );
};
